import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, euro, errMsg } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { useSeo } from "@/lib/seo";
import { ProductForm } from "@/components/admin/ProductForm";
import { ProductsTable } from "@/components/admin/ProductsTable";
import { OrdersTable } from "@/components/admin/OrdersTable";
import { InstagramKit } from "@/components/admin/InstagramKit";
import { TestimonialForm, TestimonialsTable } from "@/components/admin/Testimonials";
import { ShippingSettings } from "@/components/admin/ShippingSettings";
import { CollectionTexts } from "@/components/admin/CollectionTexts";
import { PromoCodes } from "@/components/admin/PromoCodes";
import { FormationClicks } from "@/components/admin/FormationClicks";
import { ReviewsAdmin } from "@/components/admin/ReviewsAdmin";
import { AmbassadorsAdmin } from "@/components/admin/AmbassadorsAdmin";
import { ResellersAdmin } from "@/components/admin/ResellersAdmin";
import { VisitsAdmin, VisitsToday } from "@/components/admin/VisitsAdmin";
import { ProspectsAdmin } from "@/components/admin/ProspectsAdmin";
import { GrowthAdmin } from "@/components/admin/GrowthAdmin";
import { GrowthCockpit } from "@/components/admin/GrowthCockpit";
import { SubscriptionsAdmin } from "@/components/admin/SubscriptionsAdmin";
import { SeoTracker } from "@/components/admin/SeoTracker";
import { ArticlesAdmin } from "@/components/admin/Articles";
import { AutomationAdmin } from "@/components/admin/AutomationAdmin";
import { LegacyCustomers } from "@/components/admin/LegacyCustomers";
import { ProCatalogueAdmin } from "@/components/admin/ProCatalogueAdmin";

const arr = (d) => (Array.isArray(d) ? d : []);

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">{label}</p>
      <p data-testid={testid} className="mt-3 font-display text-4xl text-[var(--gold-soft)]">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  useSeo({ title: "Tableau de bord | Ozentya", noindex: true });
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("social") ? "growth" : "products"));
  useEffect(() => {
    const s = new URLSearchParams(window.location.search).get("social");
    if (!s) return;
    const [net, res] = s.split("-");
    const label = net === "meta" ? "Instagram" : "TikTok";
    if (res === "ok") toast.success(`${label} est connecté`); else toast.error(`La connexion ${label} a échoué`);
    window.history.replaceState(null, "", "/admin");
  }, []);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tEditing, setTEditing] = useState(null);
  const [tOpen, setTOpen] = useState(false);

  const me = useQuery({ queryKey: ["me"], queryFn: async () => (await api.get("/auth/me")).data, retry: false });
  const products = useQuery({ queryKey: ["admin-products"], queryFn: async () => arr((await api.get("/admin/products")).data), enabled: !!me.data });
  const orders = useQuery({ queryKey: ["admin-orders"], queryFn: async () => arr((await api.get("/admin/orders")).data), enabled: !!me.data });
  const stats = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data, enabled: !!me.data });
  const testimonials = useQuery({ queryKey: ["admin-testimonials"], queryFn: async () => arr((await api.get("/admin/testimonials")).data), enabled: !!me.data });
  const shipCfg = useQuery({ queryKey: ["admin-shipping"], queryFn: async () => (await api.get("/admin/shipping/settings")).data, enabled: !!me.data });

  useEffect(() => {
    if (me.isError) nav("/admin/login");
  }, [me.isError, nav]);

  const refresh = () => ["admin-products", "admin-orders", "admin-stats", "products", "admin-testimonials", "testimonials", "admin-shipping"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Produit enregistré");
      setFormOpen(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Produit supprimé");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveT = async (payload) => {
    setSaving(true);
    try {
      if (tEditing) await api.put(`/admin/testimonials/${tEditing.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Témoignage enregistré");
      setTOpen(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const removeT = async (t) => {
    if (!window.confirm(`Supprimer le témoignage de « ${t.name} » ?`)) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      toast.success("Témoignage supprimé");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const fulfill = async (o, v) => {
    try {
      await api.patch(`/admin/orders/${o.id}`, { fulfillment: v });
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("ozy_admin_token");
    nav("/admin/login");
  };

  if (!me.data) return <main className="min-h-screen pt-40 text-center text-[var(--muted)]">Chargement…</main>;
  const s = stats.data || {};

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[var(--ink)]">
      <header className="flex items-center justify-between border-b border-[var(--line)] px-5 py-5 md:px-10">
        <Link to="/"><Logo /></Link>
        <div className="flex items-center gap-4 text-sm">
          <span className="hidden text-[var(--muted)] sm:inline">{me.data.email}</span>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 rounded-full border border-[var(--line)] px-4 py-2 hover:border-[var(--gold)]"><LogOut className="h-4 w-4" /> Déconnexion</button>
        </div>
      </header>
      <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-10">
        <p className="eyebrow">Espace admin</p>
        <h1 className="mt-2 font-display text-5xl italic">Tableau de bord</h1>
        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat label="Chiffre d'affaires" value={euro(s.revenue ?? 0)} testid="stat-revenue" />
          <Stat label="Commandes payées" value={s.paid_orders ?? 0} testid="stat-paid-orders" />
          <Stat label="Commandes totales" value={s.total_orders ?? 0} testid="stat-total-orders" />
          <Stat label="Produits" value={s.products ?? 0} testid="stat-products" />
        </div>
        <VisitsToday onOpen={() => setTab("visits")} />
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-b border-[var(--line)]">
          <div className="flex flex-wrap gap-x-8">
            {[["products", "Produits"], ["orders", "Commandes"], ["legacy", "Anciennes clientes"], ["subscriptions", "Abonnements"], ["promo", "Codes promo"], ["reviews", "Avis clientes"], ["ambassadors", "Ambassadeurs"], ["resellers", "Revendeurs"], ["visits", "Visites"], ["growth", "Croissance"], ["testimonials", "Avant / Après"], ["collections", "Rubriques"], ["articles", "Conseils"], ["automation", "Automatisations"], ["seo", "SEO"], ["shipping", "Expédition"], ["instagram", "Instagram"], ["procatalogue", "Catalogue pro"], ["prospects", "Prospection"]].map(([k, l]) => (
              <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`-mb-px shrink-0 whitespace-nowrap border-b-2 pb-4 font-display text-2xl transition-colors ${tab === k ? "border-[var(--gold)] italic text-[var(--gold-soft)]" : "border-transparent text-[var(--muted)]"}`}>{l}</button>
            ))}
          </div>
          {tab === "products" && (
            <button data-testid="admin-add-product" onClick={() => { setEditing(null); setFormOpen(true); }} className="mb-3 flex items-center gap-2 rounded-full bg-[var(--gold)] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] hover:bg-[var(--gold-soft)]"><Plus className="h-4 w-4" /> Ajouter</button>
          )}
          {tab === "testimonials" && (
            <button data-testid="admin-add-testimonial" onClick={() => { setTEditing(null); setTOpen(true); }} className="mb-3 flex items-center gap-2 rounded-full bg-[var(--gold)] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] hover:bg-[var(--gold-soft)]"><Plus className="h-4 w-4" /> Ajouter</button>
          )}
        </div>
        <div className="mt-8">
          {tab === "products" && (
            <ProductsTable products={products.data || []} onEdit={(p) => { setEditing(p); setFormOpen(true); }} onDelete={remove} />
          )}
          {tab === "orders" && <OrdersTable orders={orders.data || []} onFulfill={fulfill} config={shipCfg.data} onDone={refresh} />}
          {tab === "shipping" && <ShippingSettings config={shipCfg.data} onSaved={refresh} />}
          {tab === "collections" && <CollectionTexts />}
          {tab === "promo" && <><PromoCodes /><FormationClicks /></>}
          {tab === "reviews" && <ReviewsAdmin />}
          {tab === "ambassadors" && <AmbassadorsAdmin />}
          {tab === "resellers" && <ResellersAdmin />}
          {tab === "visits" && <VisitsAdmin />}
          {tab === "growth" && <><GrowthCockpit /><GrowthAdmin /></>}
          {tab === "subscriptions" && <SubscriptionsAdmin />}
          {tab === "seo" && <SeoTracker />}
          {tab === "articles" && <ArticlesAdmin />}
          {tab === "automation" && <AutomationAdmin />}
          {tab === "legacy" && <LegacyCustomers />}
          {tab === "instagram" && <InstagramKit />}
          {tab === "procatalogue" && <ProCatalogueAdmin />}
          {tab === "prospects" && <ProspectsAdmin />}
          {tab === "testimonials" && (
            <TestimonialsTable items={testimonials.data || []} onEdit={(t) => { setTEditing(t); setTOpen(true); }} onDelete={removeT} />
          )}
        </div>
      </div>
      <ProductForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSave={save} saving={saving} />
      <TestimonialForm open={tOpen} onOpenChange={setTOpen} initial={tEditing} onSave={saveT} saving={saving} />
    </main>
  );
}
